import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { sendPasswordResetEmail } from '../Services';
import {
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  InputAdornment,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LockResetIcon from '@mui/icons-material/LockReset';
import { useSnackbar } from './SnackbarProvider';

const ForgotPasswordScreen = () => {
  const [email, setEmail] = useState('');
  const navigate = useNavigate();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('sm'));
  const { showSnackbar } = useSnackbar();

  const isEmailValid = (emailVal: string) => {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(emailVal);
  };

  const handlePasswordReset = async () => {
    if (!email.trim()) {
      showSnackbar('Veuillez entrer votre adresse e-mail.', 'error');
      return;
    }

    if (!isEmailValid(email)) {
      showSnackbar('Veuillez entrer une adresse e-mail valide.', 'error');
      return;
    }

    try {
      await sendPasswordResetEmail(email);
      showSnackbar('Un e-mail de réinitialisation de mot de passe a été envoyé à votre adresse.', 'success');
      navigate(-1);
    } catch (error) {
      showSnackbar('La réinitialisation du mot de passe a échoué.', 'error');
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handlePasswordReset();
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundImage: 'url(https://beta.visamonde.com/assets/mobile/fond.jpg)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        p: 2,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          width: '100%',
          maxWidth: isDesktop ? 800 : 420,
          borderRadius: 4,
          overflow: 'hidden',
          boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
          backgroundColor: 'white',
        }}
      >
        {/* Left panel (desktop only) */}
        {isDesktop && (
          <Box
            sx={{
              flex: 1,
              background: 'linear-gradient(135deg, #2e7d32 0%, #1b5e20 100%)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              p: 5,
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            <Box
              sx={{
                position: 'absolute',
                top: -60,
                right: -60,
                width: 200,
                height: 200,
                borderRadius: '50%',
                backgroundColor: 'rgba(255,255,255,0.06)',
              }}
            />
            <Box
              sx={{
                position: 'absolute',
                bottom: -40,
                left: -40,
                width: 160,
                height: 160,
                borderRadius: '50%',
                backgroundColor: 'rgba(255,255,255,0.04)',
              }}
            />

            <LockResetIcon sx={{ fontSize: 80, color: 'rgba(255,255,255,0.3)', mb: 3 }} />
            <Typography
              variant="h5"
              sx={{ color: 'white', fontWeight: 700, textAlign: 'center', mb: 1.5, lineHeight: 1.3 }}
            >
              Mot de passe oublié ?
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: 'rgba(255,255,255,0.75)', textAlign: 'center', maxWidth: 260, lineHeight: 1.6 }}
            >
              Pas de souci, nous vous enverrons un lien de réinitialisation par email.
            </Typography>
          </Box>
        )}

        {/* Right panel - form */}
        <Box
          sx={{
            flex: 1,
            p: { xs: 3.5, sm: 5 },
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
          }}
        >
          {/* Back + Title */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 4 }}>
            <IconButton
              onClick={() => navigate(-1)}
              sx={{
                backgroundColor: '#f5f5f5',
                '&:hover': { backgroundColor: '#eee' },
                width: 40,
                height: 40,
              }}
            >
              <ArrowBackIcon sx={{ fontSize: 20, color: '#555' }} />
            </IconButton>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#333', fontSize: { xs: '1.2rem', sm: '1.4rem' } }}>
                Réinitialiser le mot de passe
              </Typography>
              <Typography variant="body2" sx={{ color: '#888', fontSize: '0.8rem' }}>
                Entrez votre adresse email
              </Typography>
            </Box>
          </Box>

          {/* Mobile logo */}
          {!isDesktop && (
            <Box sx={{ textAlign: 'center', mb: 3 }}>
              <LockResetIcon sx={{ fontSize: 56, color: '#2e7d32', mb: 1 }} />
            </Box>
          )}

          {/* Email field */}
          <TextField
            fullWidth
            variant="outlined"
            label="Adresse email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onKeyDown={handleKeyDown}
            autoComplete="email"
            type="email"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <EmailOutlinedIcon sx={{ color: '#999', fontSize: 20 }} />
                </InputAdornment>
              ),
            }}
            sx={{
              mb: 3,
              '& .MuiOutlinedInput-root': {
                borderRadius: 3,
                backgroundColor: '#f8f9fa',
                '&:hover': { backgroundColor: '#f0f1f2' },
                '&.Mui-focused': { backgroundColor: 'white' },
              },
            }}
          />

          {/* Submit */}
          <Button
            fullWidth
            variant="contained"
            onClick={handlePasswordReset}
            sx={{
              py: 1.5,
              borderRadius: 3,
              textTransform: 'none',
              fontSize: '1rem',
              fontWeight: 600,
              background: 'linear-gradient(135deg, #2e7d32 0%, #1b5e20 100%)',
              boxShadow: '0 4px 14px rgba(46,125,50,0.3)',
              '&:hover': {
                background: 'linear-gradient(135deg, #1b5e20 0%, #145218 100%)',
                boxShadow: '0 6px 20px rgba(46,125,50,0.4)',
              },
            }}
          >
            Envoyer le lien
          </Button>

          {/* Cancel */}
          <Button
            fullWidth
            variant="text"
            onClick={() => navigate(-1)}
            sx={{
              mt: 1.5,
              textTransform: 'none',
              color: '#888',
              fontWeight: 500,
              '&:hover': { color: '#555', backgroundColor: 'transparent' },
            }}
          >
            Revenir à la connexion
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default ForgotPasswordScreen;
